import { KeyboardArrowDown } from '@mui/icons-material'
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Popover,
	TextField,
	TextFieldProps,
	Typography,
} from '@mui/material'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { useEffect, useState } from 'react'

type DateFilterProps = {
	dateText?: string
	dateFrom?: Date
	dateTo?: Date
	onChange: (dateFrom?: Date, dateTo?: Date) => void
}

export const DateFilter = (props: DateFilterProps) => {
	const dateText = props.dateText ?? 'Date'

	const [dateAnchorEl, setDateAnchorEl] = useState<HTMLButtonElement | null>(
		null,
	)
	const [filterByDate, setFilterByDate] = useState(false)

	// start from the very first minute of today
	const [dateFrom, setDateFrom] = useState<Date>(
		props.dateFrom ?? moment({ hour: 0 }).toDate(),
	)

	// end at the very last minute of today
	const [dateTo, setDateTo] = useState<Date>(
		props.dateTo ??
		moment({ hour: 23, minute: 59, second: 59, ms: 999 }).toDate(),
	)

	useEffect(() => {
		props.onChange(dateFrom, dateTo)
	}, [dateFrom, dateTo])

	useEffect(() => {
		if (filterByDate) props.onChange(dateFrom, dateTo)
		else props.onChange(undefined, undefined)
	}, [filterByDate])

	return (
		<>
			<Button
				color="inherit"
				endIcon={<KeyboardArrowDown fontSize="small" />}
				onClick={(evt) => setDateAnchorEl(evt.currentTarget)}
			>
				{dateText}
			</Button>
			<Popover
				open={dateAnchorEl !== null}
				anchorEl={dateAnchorEl}
				onClose={() => setDateAnchorEl(null)}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Box display="flex" flexDirection="column" gap={1} padding={4}>
					<FormControlLabel
						control={
							<Checkbox
								checked={filterByDate}
								onChange={() => setFilterByDate(!filterByDate)}
								value={filterByDate}
							/>
						}
						label={`Filter by ${dateText}`}
					/>
					<LocalizationProvider dateAdapter={AdapterMoment}>
						<DesktopDatePicker
							disableFuture
							disabled={!filterByDate}
							value={dateFrom}
							onChange={(date: Date | null) => {
								const newDate = date !== null ? new Date(date) : undefined
								// this is so we include the entire day
								newDate?.setHours(0, 0, 0, 0)
								newDate && setDateFrom(newDate)

								// need to handle the to date if the from date is past the to
								if (
									newDate !== undefined &&
									dateTo !== undefined &&
									newDate > dateTo
								) {
									const newToDate = new Date(newDate)
									newToDate.setHours(23, 59, 59, 999)
									setDateTo(newToDate)
								}
							}}
							inputFormat="MM/DD/YYYY"
							renderInput={(params: TextFieldProps) => (
								<Box display="flex" flexDirection="column">
									<Typography variant="subtitle2">From</Typography>
									<TextField {...params} size="small" />
								</Box>
							)}
						/>
					</LocalizationProvider>
					<LocalizationProvider dateAdapter={AdapterMoment}>
						<DesktopDatePicker
							disableFuture
							disabled={!filterByDate}
							value={dateTo}
							onChange={(date: Date | null) => {
								const newDate = date !== null ? new Date(date) : undefined
								// this is so we include the entire day
								newDate?.setHours(23, 59, 59, 999)
								newDate && setDateTo(newDate)

								// need to handle the from date if the to date is before the from
								if (
									newDate !== undefined &&
									dateFrom !== undefined &&
									newDate < dateFrom
								) {
									const newFromDate = new Date(newDate)
									newFromDate.setHours(0, 0, 0, 0)
									setDateFrom(newFromDate)
								}
							}}
							inputFormat="MM/DD/YYYY"
							renderInput={(params: TextFieldProps) => (
								<Box display="flex" flexDirection="column">
									<Typography variant="subtitle2">To</Typography>
									<TextField {...params} size="small" />
								</Box>
							)}
						/>
					</LocalizationProvider>
				</Box>
			</Popover>
		</>
	)
}
